import { Icon } from "@/components/Icon"

import type { CitationReview } from "../lib/review"

interface StandardAuthoritiesModalProps {
  allegationId: string
  citations: CitationReview[]
  onClose: () => void
}

/**
 * Read-only detail view behind the "N standard authorities applied" chip:
 * the pre-verified curated-doctrine anchors the pipeline applied for this
 * allegation's governing legal standard. Purely informational — resolving or
 * adding authorities stays in CitationReviewModal.
 */
export function StandardAuthoritiesModal({
  allegationId,
  citations,
  onClose,
}: StandardAuthoritiesModalProps) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div
        className="confirm"
        style={{ maxWidth: 640, width: "min(640px, calc(100vw - 32px))" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Standard authorities applied"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>Standard authorities — {allegationId}</h3>
          <div style={{ flex: 1 }} />
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            style={{ position: "static" }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>
          Pre-verified authorities the pipeline applied for the governing legal standard of this
          allegation. They are part of the curated doctrine corpus and need no further
          verification.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {citations.map((c) => (
            <div
              key={c.citation || c.case_name}
              style={{
                border: "1px solid var(--line)",
                borderRadius: "var(--radius)",
                padding: "12px 14px",
                background: "var(--paper-2)",
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <strong>{c.case_name || c.citation}</strong>
                {c.citation && c.case_name && (
                  <span className="mono" style={{ color: "var(--ink-3)", fontSize: 12 }}>
                    {c.citation}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <span className="pill pill--ok">Pre-verified</span>
              </div>
              {(c.court || c.year) && (
                <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 4 }}>
                  {[c.court, c.year].filter(Boolean).join(" · ")}
                </div>
              )}
              {c.reason && (
                <div style={{ color: "var(--ink-2)", fontSize: 12.5, marginTop: 6 }}>
                  {c.reason}
                </div>
              )}
              {c.source_url && (
                <div style={{ marginTop: 8 }}>
                  <a
                    className="btn btn--ghost btn--sm"
                    href={c.source_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="ext" size={13} /> View source
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
